import { Fragment } from 'react';
import {
  applyDefaults,
  type Block,
  type RenderContext,
  type SectionInstance,
  type SectionSchema,
  type SettingField,
  type SettingValue,
} from '@aquilixshop/ui-react';
import { t } from '@aquilixshop/i18n';
import type { CodLayout } from '@aquilixshop/cod-config';
import { resolveLayout } from '@aquilixshop/ui-react';
import { useBuilderStore } from '../../state/builderStore';
import { FieldControl } from '../FieldControl';
import { FieldTargetProvider } from '../fieldTarget';
import {
  DEFAULT_GROUP,
  DeviceGlyph,
  groupFields,
  inheritedValue,
  isFieldVisible,
} from '../SectionForm';
import { bt, btp, groupLabel, translateFields } from '../../i18n';
import { usePaintedWidth } from './usePaintedWidth';
import { blockDisplayName } from '../../shell/sectionVariantLabel';

/**
 * The per-block auto-form (build-plan §6 / §11.4): one `FieldControl` per the block schema's fields,
 * bound to the live block `settings[field.id]`, dispatching `updateBlockSettings` with a partial keyed
 * patch on change — the same schema-driven generator the section form uses, scoped to a block.
 *
 * Deep-config infra: a field's `visibleWhen` is honoured here too (filtered against the block's own
 * settings), AND fields are partitioned by `group` exactly like the section form — ungrouped fields
 * render flat at the top, each named group renders as a labelled header + divider (Shopify parity:
 * "Desktop" / "Mobile" / "Margins"). Without this, grouped block schemas rendered as one flat list.
 */
/**
 * The buy-box's buying-journey chooser (`cod-form` schema field `orderAction`). Identified by `id` + `type`
 * rather than by block type, so it keeps working if the field moves to a sibling buy-box block.
 */
function isJourneyField(field: SettingField): boolean {
  return field.id === 'orderAction' && field.type === 'select';
}

/**
 * Show only the journeys that make sense for THIS store (founder feedback 2026-07-31 — four options, none of
 * them obviously right, is a choice nobody makes).
 *
 *  - The store sells by CARD ⇒ the buyer fills their details AND card at CHECKOUT, never on the product page
 *    (founder direction 2026-07-19). So: "Ajouter au panier" or "Acheter maintenant".
 *  - The store sells by COD ⇒ the single-page funnel, or the catalog cart. No "Acheter maintenant", which
 *    would send the buyer to a checkout with nothing to pay online.
 *
 * Keyed on the store's ONE ACTIVE method, not on whether Stripe is connected: a store with Stripe connected
 * that sells cash-on-delivery must be offered the COD journeys.
 *
 * `auto` is never listed: it resolves to the FIRST option of whichever list applies, so a page storing it
 * (every existing page — it is the schema default) already displays as that option, with no migration and no
 * behaviour change. A stored value the current store still paints is always kept in the list, so filtering
 * never silently discards a choice the seller made. A stored value the store CANNOT paint — the on-page form
 * on a CARD store — is read as `auto` instead (see `storedJourney`): displayed as what the canvas paints,
 * offered nothing else, and never rewritten.
 */
/**
 * The store's ONE active method as `loadStore()` publishes it: `null` = not answered yet, or the read failed.
 * `null` is NOT "COD". It was read that way until 23/09, and on a card store the chooser opened on THREE
 * journeys — the COD pair, plus the page's own "Acheter maintenant" kept by the filter — then snapped to
 * two once the answer landed. A list we cannot vouch for is not offered: unknown lists nothing to change.
 */
type StorePaymentMethod = 'CARD' | 'COD' | null;

function journeyKeys(method: StorePaymentMethod): readonly string[] {
  if (method === null) return [];
  return method === 'CARD' ? ['add-to-cart', 'buy-now'] : ['cod', 'add-to-cart'];
}

/**
 * The journey the page explicitly stores AND this store can paint — `auto`, "nothing", and the on-page form
 * on a CARD store all mean "follow the store". The last one is the founder's page of 28/09/2026: `'cod'`
 * stored while the store sold cash on delivery, the store taking cards since. A card store has no on-page
 * order form (`resolveOrderAction`, rule 3), so the canvas paints the cart journey there; the box must read
 * the same — the card pair offered, the form's own settings hidden — instead of opening « comme en COD » on
 * three journeys until the seller changes the value once. The stored value is left in place: it is not a
 * choice to discard, just one this store cannot render today.
 */
function storedJourney(raw: unknown, method: StorePaymentMethod): string | undefined {
  if (typeof raw !== 'string' || raw === 'auto') return undefined;
  if (raw === 'cod' && method === 'CARD') return undefined;
  return raw;
}

function journeyOptionsFor(
  field: SettingField,
  method: StorePaymentMethod,
  current: unknown,
): SettingField {
  if (!isJourneyField(field) || field.type !== 'select') return field;
  const keep = journeyKeys(method);
  // Unknown store: the ONE option the control displays (the stored journey, or `auto` itself), so the seller
  // can read what the page holds but cannot pick from a list that may be the wrong store's.
  const shown = storedJourney(current, method) ?? (method === null ? 'auto' : undefined);
  const options = field.options.filter((o) => keep.includes(o.value) || o.value === shown);
  return options.length ? { ...field, options } : field;
}

/**
 * What the journey control DISPLAYS. A page storing `auto` (the schema default, so nearly all of them) has no
 * matching option once `auto` is unlisted, and the control would render blank. Show the option `auto` actually
 * resolves to — the first of this store's list — WITHOUT writing it: untouched pages keep storing `auto`, so
 * their behaviour is identical and a store that later connects Stripe still re-resolves on its own.
 */
function journeyValue(
  field: SettingField,
  method: StorePaymentMethod,
  raw: SettingValue | undefined,
) {
  if (!isJourneyField(field)) return raw;
  // No store answer yet: say `auto`, which is what the page stores — not the first key of a guessed list.
  return storedJourney(raw, method) ?? journeyKeys(method)[0] ?? 'auto';
}

/**
 * What the form-presentation control DISPLAYS. `layout` moved from the COD app to this block and carries no
 * schema default, so a page that predates the move stores nothing and the control would render blank. Show
 * the value the block actually renders — the app's stored layout — WITHOUT writing it, so untouched pages
 * keep inheriting and a change in the app still reaches them.
 */
function layoutValue(field: SettingField, appLayout: CodLayout, raw: SettingValue | undefined) {
  if (field.id !== 'layout' || field.type !== 'select') return raw;
  return resolveLayout(raw, appLayout);
}

/**
 * The order button's text control SHOWS the app's label greyed in the box while the block stores nothing:
 * the seller sees what the button actually reads before deciding to give this page its own (founder,
 * 2026-09-21). Placeholder, never value — an untouched block keeps following the app.
 */
function buttonLabelField(
  field: SettingField,
  placeholder: string,
  info: string | undefined,
): SettingField {
  if (field.id !== 'buttonLabel' || field.type !== 'text') return field;
  return { ...field, placeholder, ...(info === undefined ? {} : { info }) };
}

/**
 * What the order button READS while the block stores no text of its own — the text the canvas paints, so the
 * control shows it greyed (founder, 2026-09-21), on EVERY journey (founder, 24/09/2026: on her card store
 * the « Acheter maintenant » button could not be reworded, the control being hidden off the on-page funnel).
 *
 *  - On-page funnel (a COD store, or one that has not answered yet): the COD app's store-wide text.
 *  - Catalog journeys: the journey's own wording, « Acheter maintenant » / « Ajouter au panier ».
 *
 * A CARD store never reaches the funnel line: `effectiveJourney` reads its pages as a catalog journey, so
 * the « Payer par carte » this showed until 28/09/2026 went with the on-page card funnel it described. The
 * catalog phrases are STOREFRONT phrases in the SHOP's language (what the buyer reads), hence `t()` on the
 * canvas's own render context, never a builder-chrome phrase.
 */
function buttonDefault(
  journey: string,
  appButtonLabel: string,
  ctx: RenderContext | null,
  storeLanguage: string,
): { placeholder: string; fromApp: boolean } {
  const shop = ctx ?? { locale: storeLanguage };
  if (journey === 'buy-now') return { placeholder: t(shop, 'product.buy_now'), fromApp: false };
  if (journey === 'add-to-cart')
    return { placeholder: t(shop, 'product.add_to_cart'), fromApp: false };
  return { placeholder: appButtonLabel, fromApp: true };
}

/**
 * The fields that only describe the ON-PAGE order form — how it is presented, and its heading.
 *
 * They are meaningless on the catalog journeys: with "Ajouter au panier" or "Acheter maintenant" the buy-box
 * renders a single CTA and the buyer fills their details at CHECKOUT, so there is no form on the page to show
 * inline or in a popup, and no heading above it (founder feedback 2026-08-01). Asking anyway is exactly the
 * "don't show choices that aren't feasible" rule this inspector already applies to the journey list itself.
 *
 * Hidden, never cleared: a seller who picked "Popup" then switched to the cart journey gets it back untouched
 * on switching home.
 *
 * The button TEXT is not in this list: every journey ends in an order button, and the text is that button's
 * (founder, 24/09/2026). See `buttonDefault` for what it inherits on each journey.
 */
const FORM_ONLY_FIELDS: readonly string[] = ['layout', 'formTitle'];

/**
 * The journey the block ACTUALLY renders — the stored value, or what `auto` resolves to for this store. The
 * schema alone cannot answer this (`visibleWhen` compares raw settings and would read `auto` as "not cod"),
 * which is why the gate lives here, where the store's active method is known.
 */
function effectiveJourney(block: Block, method: StorePaymentMethod): string {
  // Unknown store → `'cod'`, the funnel the CANVAS paints while it waits (`resolveOrderAction` with no
  // checkout style): the form-only fields describe what the seller sees, so they follow the canvas.
  return storedJourney(block.settings['orderAction'], method) ?? journeyKeys(method)[0] ?? 'cod';
}

export function BlockInspector({
  section,
  schema,
  block,
  hideTitle = false,
}: {
  section: SectionInstance;
  schema: SectionSchema;
  block: Block;
  /** Suppress the internal block-name heading when the host (the Inspector panel) already shows it. */
  hideTitle?: boolean;
}) {
  const updateBlockSettings = useBuilderStore((s) => s.updateBlockSettings);
  const selectSection = useBuilderStore((s) => s.selectSection);
  const selectBlock = useBuilderStore((s) => s.selectBlock);
  // The store's ONE active method (null = not known yet / fetch failed → the journey chooser offers nothing
  // to change until the store has answered; see `journeyKeys`).
  const method = useBuilderStore((s) => s.storePaymentMethod);
  // The COD app's stored presentation — what a block inheriting it will render.
  const appLayout = useBuilderStore((s) => s.codConfig.layout);
  // …and its order-button text, the default this block's own text control overrides.
  const appButtonLabel = useBuilderStore((s) => s.codConfig.buttonLabel);
  // The canvas's own context (its locale is the SHOP's), so the button-text control can say what the canvas
  // paints in the shop language — not what the builder chrome would say in the seller's.
  const renderContext = useBuilderStore((s) => s.renderContext);
  const storeLanguage = useBuilderStore((s) => s.storeLanguage);
  // THE BOX THE PAGE ACTUALLY PAINTED (05/10/2026). A text block's width is a number of pixels, and its
  // column grows to hold it — but a section has an edge. Past that edge the slider moved and nothing on
  // the page did (« after I depasse 400px it doesn't work anymore »), so the row now says what was
  // painted, in the frame she is looking at: the desktop pair under the desktop/tablet frame, the phone
  // pair under the phone frame. Hooks first — above the early return below (React 310 otherwise).
  const device = useBuilderStore((s) => s.device);
  const widthFieldId = device === 'mobile' ? 'mobileWidthPx' : 'desktopWidthPx';
  const askedWidth = block.type === 'text' ? block.settings[widthFieldId] : undefined;
  const painted = usePaintedWidth(
    typeof askedWidth === 'number' && askedWidth > 0 ? `[data-aqx-page] .aqx-tx-${block.id}` : null,
    `${String(askedWidth)}|${device}`,
  );
  const cappedHint =
    typeof askedWidth === 'number' && painted !== null && painted < askedWidth - 1
      ? btp(
          'bld.range.painted_capped',
          'Painted at {n} px: the section is not wider in this preview.',
          { n: String(painted) },
        )
      : undefined;
  const withPaintedHint = (field: SettingField): SettingField =>
    cappedHint !== undefined && field.id === widthFieldId ? { ...field, info: cappedHint } : field;
  const bschema = schema.blocks?.find((b) => b.type === block.type);

  if (!bschema) {
    return (
      <p className="bld-field__info">
        {bt('bld.unknown_block', "This block type isn't editable here.")}
      </p>
    );
  }

  const journey = effectiveJourney(block, method);
  const buttonText = buttonDefault(journey, appButtonLabel, renderContext, storeLanguage);
  // Off the COD app's text, the schema's "Empty = the text set in the Cash on delivery app" would be a lie.
  const buttonInfo = buttonText.fromApp
    ? undefined
    : bt(
        'bld.buybox_button_text_journey_info',
        'Empty = this journey’s own wording, in the shop language. Each page can have its own.',
      );
  const renderField = (field: (typeof bschema.fields)[number]) => (
    <Fragment key={field.id}>
      <FieldControl
        field={withPaintedHint(
          buttonLabelField(
            journeyOptionsFor(field, method, block.settings[field.id]),
            buttonText.placeholder,
            buttonInfo,
          ),
        )}
        value={layoutValue(field, appLayout, journeyValue(field, method, block.settings[field.id]))}
        inherited={inheritedValue(field, bschema.fields, block.settings)}
        onChange={(next: SettingValue | undefined) =>
          updateBlockSettings(section.id, block.id, { [field.id]: next as SettingValue })
        }
      />
    </Fragment>
  );

  // visibleWhen must see what the CONTROLS display: raw block settings miss every key that pre-dates
  // a schema field (block settings are never default-filled at hydrate, unlike sections), while the
  // controls render `value ?? default` — so a `{ field: 'showTable', equals: true }` gate hid its
  // fields on old docs even though "Show table" showed checked. Evaluate on defaults-applied settings.
  const effectiveSettings = applyDefaults(bschema.fields, block.settings);
  // …and the SECTION's, for the `scope: 'section'` clauses: a block field that only means something under
  // certain section settings (a comparison-table row's "Competitor 2" tick, which paints nothing while the
  // table displays one competitor) hides itself instead of lying about what it does.
  const sectionSettings = applyDefaults(schema.fields, section.settings);
  const onPageForm = journey === 'cod';
  // Same seam as SectionForm, addressed by BLOCK type. `isFieldVisible` and `FORM_ONLY_FIELDS` still
  // read `field.id`, which translation never touches (feature 0051 lot 4).
  const visible = translateFields(block.type, bschema.fields, section.type).filter(
    (field) =>
      isFieldVisible(field, effectiveSettings, sectionSettings) &&
      (onPageForm || !FORM_ONLY_FIELDS.includes(field.id)),
  );
  const groups = groupFields(visible);
  const defaultGroup = groups.find(([name]) => name === DEFAULT_GROUP)?.[1] ?? [];
  const namedGroups = groups.filter(
    ([name, fields]) => name !== DEFAULT_GROUP && fields.length > 0,
  );

  return (
    // The block's address, so an upload started here survives this panel being closed and can be
    // brought back into view from the topbar if it fails (see `lib/mediaUploads.ts`).
    <FieldTargetProvider
      target={`block:${section.id}:${block.id}`}
      reveal={() => {
        selectSection(section.id);
        selectBlock(block.id);
      }}
    >
      {!hideTitle && (
        <p className="bld-blocks__title">
          {blockDisplayName(section.type, block.type, bschema.name)}
        </p>
      )}
      {defaultGroup.length > 0 && <div className="bld-form">{defaultGroup.map(renderField)}</div>}
      {/* Same device parts as a section's own form — a block that lays its settings out per device
          (none today, by design: the two media SECTIONS came first) gets the glyph for free. */}
      {namedGroups.map(([name, fields]) => {
        const device = name === 'Desktop' ? 'desktop' : name === 'Mobile' ? 'mobile' : undefined;
        return (
          <section
            key={name}
            className={`bld-grp${device ? ' bld-grp--device' : ''}`}
            data-device={device}
          >
            <h3 className="bld-grp__title">
              {device && <DeviceGlyph device={device} />}
              {groupLabel(block.type, name)}
            </h3>
            <div className="bld-form">{fields.map(renderField)}</div>
          </section>
        );
      })}
    </FieldTargetProvider>
  );
}
